// AIインポート一括取消履歴 — 在庫・技術資産・IT資産・備品の全バッチを一覧し、
// 古いインポートでもワンタップでロールバックできる管理画面。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClockCounterClockwise, ArrowUUpLeft, CheckCircle, XCircle, Paperclip,
  CircleNotch, ShieldWarning,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'mfg', label: '在庫（製造）' },
  { key: 'eng', label: '技術資産' },
  { key: 'it', label: 'IT資産' },
  { key: 'bihin', label: '備品' },
];

const MODE_LABEL = { new_only: '新規のみ', allow_update: '既存更新あり' };

const listUrl = (tab) => (tab === 'mfg'
  ? `${API}/api/mfg/inventory/ai-import/batches?limit=100`
  : `${API}/api/asset-ai-import/${tab}/batches`);
const undoUrl = (tab, id) => (tab === 'mfg'
  ? `${API}/api/mfg/inventory/ai-import/batches/${id}/undo`
  : `${API}/api/asset-ai-import/${tab}/batches/${id}/undo`);

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch (_e) { return iso; }
};

export default function AiImportBatches() {
  const [tab, setTab] = useState('mfg');
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  const [undoing, setUndoing] = useState(null);

  const load = useCallback(() => {
    setRows(null); setDenied(false);
    axios.get(listUrl(tab), { withCredentials: true })
      .then((r) => setRows(r.data || []))
      .catch((e) => {
        if (e?.response?.status === 403) setDenied(true);
        setRows([]);
      });
  }, [tab]);

  useEffect(load, [load]);

  const doUndo = async (b) => {
    const n = b.item_count ?? (b.item_ids || []).length;
    if (!window.confirm(`このインポート（${fmtDT(b.created_at)} ・ ${n} 件）を一括取消しますか？\n在庫・使用履歴のある品目はスキップされ、残りはアーカイブされます。`)) return;
    setUndoing(b.id);
    try {
      const res = (await axios.post(undoUrl(tab, b.id), null, { withCredentials: true })).data;
      const s = res.summary || res.undo_summary || {};
      toast.success(`一括取消が完了しました — アーカイブ ${s.archived ?? '—'} 件 / スキップ ${s.skipped ?? 0} 件`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括取消に失敗しました');
    } finally { setUndoing(null); }
  };

  return (
    <div className="space-y-4" data-testid="ai-import-batches-page">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
          <ClockCounterClockwise size={22} weight="duotone" className="text-violet-600" />
          AIインポート履歴・一括取消
        </h1>
        <p className="text-[12.5px] text-zinc-500 mt-1">
          過去のAIインポートをモジュール別に一覧します。古いインポートもここから一括取消（ロールバック）できます。
          在庫・貸出・使用履歴のある品目は安全のためスキップされます。
        </p>
      </header>

      <div className="flex items-center gap-1.5 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            data-testid={`batches-tab-${t.key}`}
            className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key ? 'bg-violet-600 border-violet-600 text-white'
                : 'bg-white border-zinc-200 text-zinc-600 hover:bg-violet-50'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        {rows === null && <p className="px-4 py-8 text-center text-[12px] text-zinc-400">読み込み中…</p>}
        {rows !== null && denied && (
          <p className="px-4 py-8 text-center text-[12px] text-amber-700 flex items-center justify-center gap-1.5" data-testid="batches-denied">
            <ShieldWarning size={15} /> このモジュールの履歴を見る権限がありません。
          </p>
        )}
        {rows !== null && !denied && rows.length === 0 && (
          <p className="px-4 py-10 text-center text-[12px] text-zinc-400" data-testid="batches-empty">
            このモジュールのAIインポート履歴はまだありません。
          </p>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-[12px]">
              <thead className="bg-zinc-50 text-[10.5px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">実行日時</th>
                  <th className="text-left px-4 py-2 font-semibold">ファイル / ソース</th>
                  <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">モード</th>
                  <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">実行者</th>
                  <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">登録件数</th>
                  <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">状態</th>
                  <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const undone = b.undo_status === 'undone';
                  const n = b.item_count ?? (b.item_ids || []).length;
                  return (
                    <tr key={b.id} className="border-t border-zinc-200 hover:bg-violet-50/30" data-testid={`batch-row-${b.id}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-zinc-700">{fmtDT(b.created_at)}</td>
                      <td className="px-4 py-2.5 text-zinc-600 max-w-[240px]">
                        <span className="flex items-center gap-1 truncate">
                          {b.file_name && <Paperclip size={11} className="shrink-0 text-zinc-400" />}
                          <span className="truncate" title={b.file_name || b.source}>{b.file_name || b.source || '—'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-zinc-600">{MODE_LABEL[b.mode] || b.mode || '—'}</td>
                      <td className="px-4 py-2.5 text-zinc-600 truncate max-w-[140px]">{b.actor_name || b.actor_email || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-zinc-800">
                        {n}
                        {(b.updated_item_ids || []).length > 0 && (
                          <span className="text-[10px] text-zinc-400 font-normal"> (+更新{b.updated_item_ids.length})</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {undone ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-500">
                            <XCircle size={11} weight="fill" /> 取消済み
                            {b.undo_summary && <span className="font-normal">（{b.undo_summary.archived}件）</span>}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">
                            <CheckCircle size={11} weight="fill" /> 有効
                          </span>
                        )}
                        {undone && b.undone_at && (
                          <span className="block text-[10px] text-zinc-400 mt-0.5">
                            {fmtDT(b.undone_at)}
                            {(() => {
                              const who = typeof b.undone_by === 'string' ? b.undone_by
                                : b.undone_by?.name || b.undone_by?.email || '';
                              return who ? ` · ${who}` : '';
                            })()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {!undone && (
                          <button type="button" onClick={() => doUndo(b)} disabled={undoing === b.id}
                            data-testid={`batch-undo-${b.id}`}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10.5px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                            {undoing === b.id ? <CircleNotch size={11} className="animate-spin" /> : <ArrowUUpLeft size={11} />}
                            一括取消
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
